import { ListOfPromotion } from '../../domain/ListOfPromotion'
import type { Config } from '../../../_config'
import { FactoryFetcher } from '../../../_fetcher/FactoryFetcher'
import { DomainError } from '../../../_kernel/DomainError'
import { ErrorsCode } from '../../../_kernel/ErrorsCode'
import type { Language } from '../../../shared/domain/Language'
import type { Market } from '../../../shared/domain/Market'
import { ListOfBanner } from '../../domain/ListOfBanner'
import { ListOfTopDestination } from '../../domain/ListOfTopDestination'
import type {
  GetCurrentPromoInput,
  GetTopDestinationInput,
  MarketingRepository,
} from '../MarketingRepository'
import {
  GETTopDestinationsByBannerIdQuery,
  GETTopDestinationsByBannerIdResponse,
} from './schemas/banners/:id/destinations'
import type { GETBannerInformationResponseType } from './schemas/banners/banners-json'
import { GETBannerInformationResponseLenient } from './schemas/banners/banners-json'
import { GETActiveBannersMarketingQuery } from './schemas/marketing/banners'
import type { GETActiveBannersMarketingResponseType } from './schemas/marketing/banners'
import {
  GETCurrentPromoMarketingQuery,
  GETCurrentPromoMarketingResponse,
} from './schemas/promotions/active'
import type { FeatureFlag } from '../../../shared/domain/FeatureFlag'

export class HTTPMarketingRepository implements MarketingRepository {
  static create({ config }: { config: Config }) {
    return new HTTPMarketingRepository(config)
  }

  constructor(private config: Config) {}

  async getActivePromotions({ market, language }: GetCurrentPromoInput): Promise<ListOfPromotion> {
    const fetcher = await FactoryFetcher.create({ config: this.config }).fetcher()
    const API_GATEWAY = this.config.get('API_GATEWAY')

    const [err, json] = await fetcher.get<GETCurrentPromoMarketingResponse>(
      `${API_GATEWAY}/promotions/active?market=${market.value}&language=${language.value}`,
      {
        schemas: {
          response: GETCurrentPromoMarketingResponse,
          query: GETCurrentPromoMarketingQuery,
        },
      }
    )
    if (err) throw DomainError.withCodes(ErrorsCode.LIST_PROMOTIONS_NOT_FOUND).withCauses(err)

    return ListOfPromotion.fromAPI({ json, market, language })
  }

  async getTopDestination({
    bannerId,
    market,
    lang,
  }: GetTopDestinationInput): Promise<ListOfTopDestination> {
    const fetcher = await FactoryFetcher.create({ config: this.config }).fetcher()
    const API_GATEWAY = this.config.get('API_GATEWAY')

    const [err, json] = await fetcher.get<GETTopDestinationsByBannerIdResponse>(
      `${API_GATEWAY}/v1/banners/${bannerId.value}/destinations?market=${market.value}&lang=${lang.value}`,
      {
        schemas: {
          response: GETTopDestinationsByBannerIdResponse,
          query: GETTopDestinationsByBannerIdQuery,
        },
      }
    )

    if (err) throw DomainError.withCodes(ErrorsCode.LIST_TOP_DESTINATIONS_NOT_FOUND)

    return ListOfTopDestination.fromAPI({ json })
  }

  async getBanners({
    market,
    lang,
    bannerServiceFeatureFlag,
  }: {
    market: Market
    lang: Language
    bannerServiceFeatureFlag: FeatureFlag
  }) {
    if (bannerServiceFeatureFlag.isEnabled()) {
      return this.getCurrentBanners({ market, lang })
    }

    return this.getLegacyBanners({ market, lang })
  }

  private async getLegacyBanners({ market, lang }: { market: Market; lang: Language }) {
    const fetcher = await FactoryFetcher.create({ config: this.config }).fetcher()

    const staticsHost = this.config.get('STATICS_HOST')
    const bannersFileName = this.config.get('BANNERS_FILE_NAME')

    const [err, json] = await fetcher.get<GETBannerInformationResponseLenient>(
      `${staticsHost}/banners/${bannersFileName}`,
      {
        schemas: {
          response: GETBannerInformationResponseLenient,
        },
      }
    )

    if (err) {
      return ListOfBanner.empty()
    }

    return ListOfBanner.fromLegacyApi({
      list: json.banners as GETBannerInformationResponseType['banners'],
      market,
      language: lang,
    })
  }

  private async getCurrentBanners({ market, lang }: { market: Market; lang: Language }) {
    const fetcher = await FactoryFetcher.create({ config: this.config }).fetcher()

    const apiGateway = this.config.get('API_GATEWAY')

    const [err, json] = await fetcher.get<GETActiveBannersMarketingResponseType>(
      `${apiGateway}/marketing/banners?market=${market.value}&language=${lang.value}`,
      {
        schemas: {
          response: GETBannerInformationResponseLenient,
          query: GETActiveBannersMarketingQuery,
        },
      }
    )

    if (err) {
      return ListOfBanner.empty()
    }

    return ListOfBanner.fromLegacyApi({
      list: json.banners as GETBannerInformationResponseType['banners'],
      market,
      language: lang,
    })
  }
}
